import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTwa } from '@/app/TwaProvider';
import { deepLinkPath } from '@/lib/startParam';

// One hand-off per tab, not per mount. A ref alone would re-fire the jump on
// every reload — `start_param` is still there, so refreshing while reading the
// Mine page would yank the user back to the deep link's target.
const ROUTED_KEY = 'hg.startapp-routed';

function alreadyRouted(): boolean {
  try { return sessionStorage.getItem(ROUTED_KEY) === '1'; } catch { return false; }
}

function markRouted(): void {
  try { sessionStorage.setItem(ROUTED_KEY, '1'); } catch { /* storage unavailable */ }
}

/**
 * Opens the screen named by `startapp`, once, after sign-in has settled.
 *
 * `t.me/<bot>?startapp=deposit` should land on the deposit page with the user
 * already signed in. Two things have to be waited out first:
 *
 *   - The automatic sign-in. Every deep-link target so far sits behind
 *     RequireAuth, so navigating mid-handshake would bounce the user to the
 *     sign-in page a moment before their session arrives.
 *   - start_param arriving. It is not always there on the first render — some
 *     clients publish it a beat after the session itself — so an absent value
 *     is never read as "no deep link"; the jump just waits for one.
 *
 * @param signingIn Whether the automatic sign-in is still in flight.
 */
export function useStartParamRoute(signingIn: boolean): void {
  const { startParam } = useTwa();
  const navigate = useNavigate();
  const routed = useRef(false);

  useEffect(() => {
    if (routed.current || signingIn) return;

    const path = deepLinkPath(startParam);
    // No target yet. Deliberately NOT treated as "no target at all": absence
    // is never conclusive here.
    //
    // This used to give up as soon as TwaProvider's `detecting` cleared, which
    // looked right and was not — that flag goes down the moment a SESSION is
    // established, while start_param is hunted separately for up to another
    // fifteen seconds. A client that published the two a beat apart therefore
    // settled on "no deep link" a beat before the deep link arrived, and the
    // user sat on Home. Reloading fixed it only because the warm SDK then had
    // start_param ready on the first read.
    //
    // So the question simply stays open. `startParam` is a dependency, so a
    // value landing later re-runs this and the jump happens then; a value that
    // never lands costs nothing but a comparison against null.
    if (!path) return;

    routed.current = true;
    if (alreadyRouted()) return;
    markRouted();
    // `replace` so the jump leaves no entry behind: pressing back from the
    // target should leave for Home, not return to a route the user never saw.
    navigate(path, { replace: true });
  }, [startParam, signingIn, navigate]);
}
